import {
  Alert,
  Button,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Link as RouterLink, useSearchParams } from "react-router-dom";

import { useDI } from "@/infrastructure/di/useDI";
import {
    AuthBrand,
  AuthCard,
  AuthContent,
  AuthHeader,
  AuthInfoPanel,
  AuthPage,
} from "../layout";
import { useResetPasswordViewModel } from "./ResetPasswordViewModel";

export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token")?.trim() ?? "";

  const { resetPassword } = useDI();

  const vm = useResetPasswordViewModel({
    token,
    resetPassword,
  });

  return (
    <AuthPage>
      <AuthCard>
        <AuthBrand />
        <AuthContent>
          <AuthHeader
            title="Neues Passwort setzen"
            subtitle="Wählen Sie ein neues Passwort für Ihr Logami-Konto."
          />

          <Stack spacing={3}>
            {!vm.hasToken && (
              <Stack spacing={2}>
                <Alert severity="error">
                  Dieser Link zum Zurücksetzen ist ungültig, da der Token fehlt.
                </Alert>

                <Button
                  component={RouterLink}
                  to="/password/reset/init"
                  variant="contained"
                  size="large"
                >
                  Neuen Link anfordern
                </Button>
              </Stack>
            )}

            {vm.hasToken && vm.submitState.type === "changed" && (
              <Stack spacing={2}>
                <Alert severity="success">
                  Ihr Passwort wurde erfolgreich geändert.
                </Alert>

                <AuthInfoPanel>
                  <Stack spacing={1}>
                    <Typography fontWeight={600}>
                      Sie können sich jetzt anmelden
                    </Typography>

                    <Typography variant="body2" color="text.secondary">
                      Verwenden Sie ab sofort Ihr neues Passwort.
                    </Typography>
                  </Stack>
                </AuthInfoPanel>

                <Button
                  component={RouterLink}
                  to="/login"
                  variant="contained"
                  size="large"
                >
                  Zur Anmeldung
                </Button>
              </Stack>
            )}

            {vm.hasToken && vm.submitState.type !== "changed" && (
              <Stack
                component="form"
                spacing={3}
                onSubmit={(event) => {
                  event.preventDefault();
                  void vm.actions.submit();
                }}
              >
                {vm.submitState.type === "failed" && (
                  <Alert severity="error">{vm.submitState.message}</Alert>
                )}

                <Stack spacing={2}>
                  <TextField
                    label="Neues Passwort"
                    type="password"
                    value={vm.values.password}
                    onChange={(event) =>
                      vm.actions.setField("password", event.target.value)
                    }
                    error={Boolean(vm.fieldErrors.password)}
                    helperText={
                      vm.fieldErrors.password ??
                      "Mindestens 12 Zeichen empfohlen."
                    }
                    autoComplete="new-password"
                    fullWidth
                    autoFocus
                  />

                  <TextField
                    label="Neues Passwort bestätigen"
                    type="password"
                    value={vm.values.confirmPassword}
                    onChange={(event) =>
                      vm.actions.setField(
                        "confirmPassword",
                        event.target.value,
                      )
                    }
                    error={Boolean(vm.fieldErrors.confirmPassword)}
                    helperText={vm.fieldErrors.confirmPassword}
                    autoComplete="new-password"
                    fullWidth
                  />
                </Stack>

                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={!vm.canSubmit}
                >
                  {vm.submitState.type === "submitting" ? (
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <CircularProgress size={18} />
                      <span>Passwort wird geändert...</span>
                    </Stack>
                  ) : (
                    "Passwort ändern"
                  )}
                </Button>

                <Button
                  component={RouterLink}
                  to="/password/reset/init"
                  variant="text"
                >
                  Neuen Link anfordern
                </Button>
              </Stack>
            )}
          </Stack>
        </AuthContent>
      </AuthCard>
    </AuthPage>
  );
}
